import { useState, useEffect } from 'react';
import { productService } from '../../services/productService';
import type { Product, CreateProductRequest, UpdateProductRequest } from '../../types/product';
import { MIN_SEARCH_LENGTH } from '../../utils/searchConstants';

interface UseProductDataParams {
  companyId: number | null;
}

export function useProductData({ companyId }: UseProductDataParams) {
  // State
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  // Filters
  const [filterName, setFilterName] = useState('');
  const [filterCode, setFilterCode] = useState('');
  const [debouncedName, setDebouncedName] = useState('');
  const [debouncedCode, setDebouncedCode] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showOnlyActive, setShowOnlyActive] = useState(true);

  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [productToEdit, setProductToEdit] = useState<Product | null>(null);

  // Debounce filters
  useEffect(() => {
    if (filterName.length > 0 && filterName.length < MIN_SEARCH_LENGTH) return;
    const timer = setTimeout(() => {
      setDebouncedName(filterName);
      setDebouncedCode(filterCode);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [filterName, filterCode]);

  // Fetch products
  useEffect(() => {
    const fetchData = async () => {
      if (!companyId) {
        setError('Ідентифікатор компанії відсутній');
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await productService.search(companyId, {
          name: debouncedName || undefined,
          code: debouncedCode || undefined,
          includeArchived: !showOnlyActive,
          page: page - 1,
          size: 10,
        });
        setProducts(response.content);
        setTotalPages(response.totalPages);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Не вдалося завантажити продукти');
      } finally {
        setLoading(false);
        setIsFirstLoad(false);
      }
    };

    fetchData();
  }, [companyId, page, debouncedName, debouncedCode, showOnlyActive]);

  // CRUD handlers
  const handleCreateProduct = async (productData: CreateProductRequest) => {
    if (!companyId) return;

    const newProduct = await productService.create(productData);
    setProducts((prev) => [...prev, newProduct]);
  };

  const handleUpdateProduct = async (id: number, productData: UpdateProductRequest) => {
    const updatedProduct = await productService.update(id, productData);
    setProducts((prev) => prev.map((p) => (p.id === id ? updatedProduct : p)));
  };

  const handleDeleteProduct = async (productId: number) => {
    if (!window.confirm('Ви впевнені, що хочете архівувати цей продукт?')) {
      return;
    }

    await productService.delete(productId);
    setProducts((prev) => (
      showOnlyActive
        ? prev.filter((p) => p.id !== productId)
        : prev.map((p) => (p.id === productId ? { ...p, deactivatedDate: new Date().toISOString() } : p))
    ));
  };

  const handleRestoreProduct = async (productId: number) => {
    await productService.restore(productId);
    setProducts((prev) => prev.map((p) => (
      p.id === productId ? { ...p, deactivatedDate: undefined } : p
    )));
  };

  const handleEditClick = (product: Product) => {
    setProductToEdit(product);
    setEditDialogOpen(true);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleShowOnlyActiveChange = (value: boolean) => {
    setShowOnlyActive(value);
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilterName('');
    setFilterCode('');
    handleShowOnlyActiveChange(true);
  };

  return {
    data: {
      products,
      loading,
      error,
      isFirstLoad,
    },
    pagination: {
      page,
      count: totalPages,
      onChange: handlePageChange,
    },
    filters: {
      filterName,
      filterCode,
      debouncedName,
      debouncedCode,
      showFilters,
      showOnlyActive,
      setFilterName,
      setFilterCode,
      setShowFilters,
      setShowOnlyActive: handleShowOnlyActiveChange,
      handleClearFilters,
    },
    crud: {
      handleCreateProduct,
      handleUpdateProduct,
      handleDeleteProduct,
      handleRestoreProduct,
      handleEditClick,
    },
    dialogs: {
      createDialogOpen,
      setCreateDialogOpen,
      editDialogOpen,
      setEditDialogOpen,
      productToEdit,
      setProductToEdit,
    },
  };
}
